import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';


class Scene {
    constructor({
            container = document.querySelector('[data-target="webgl-container"]'),
            orbitControlsEnabled = true,
        } = {}) {
        this.container = container;
        this.renderer = this.createRenderer(container);
        this.scene = this.createScene();
        this.render = true;
        this.camera = this.createCamera();
        this.orbitControls = orbitControlsEnabled && this.createOrbitControls();
        this.loadingManager = new THREE.LoadingManager();
        this.sceneGroup = new THREE.Group();
        // this.lights = this.createLights();
        this.lights = {};
        this.animations = [];
        this.clock = new THREE.Clock();
        this.mixer = null;
        this.clipActions = [];
        // this.stats = new stats();
        this.mouseX = 0;
        this.mouseY = 0;
        this.mouseDown = false;
        this.sceneRotate = false;
        this.data = {} // custom variables, controlled here for easy access by dat.gui
        this.audioTracks = {};

        // setup
        this.init();

        // start animation loop
        this.animate();
    }

    init() {
        this.scene.add(this.sceneGroup);

        this.camera.position.set(0, 5, 10); // Adjust as needed
        
        // update mouse positions on mousemove
        window.addEventListener('mousemove', event => {
            this.mouseX = event.clientX - window.innerWidth / 2;
            this.mouseY = event.clientY - window.innerHeight / 2;
        });

        // set click & holding events
        this.container.addEventListener('pointerdown', event => this.mouseDown = true );
        this.container.addEventListener('pointerup', event => this.mouseDown = false);
    }

    createScene() {
        // init
        const scene = new THREE.Scene();

        // setup
        scene.background = null;

        return scene;
    }

    createRenderer() {
        // init
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true});

        // setup
        renderer.setSize(this.container.offsetWidth, this.container.offsetHeight);
        renderer.setClearColor( 0x000000, 0 );
        renderer.setPixelRatio( Math.min( window.devicePixelRatio, 2 ) );
        renderer.outputColorSpace = THREE.LinearSRGBColorSpace;

        // add to DOM
        this.container.appendChild(renderer.domElement);

        // handle window resize
        window.addEventListener('resize', () => {
            const width = this.container.offsetWidth * 1.5;
            const height = this.container.offsetHeight;
            this.renderer.setSize(width, height);
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        });

        return renderer;
    }

    createCamera() {
        // init
        const camera = new THREE.PerspectiveCamera(
            75,
        this.container.offsetWidth * 1.5 / this.container.offsetHeight,
        0.01,
        1000
        );

        camera.position.set(0, 5, 10);

        return camera;
    }

    createLights() {
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        this.scene.add(ambientLight);

        return [ambientLight];
    }

    createOrbitControls() {
        const controls = new OrbitControls(this.camera, this.renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.125;
        controls.update();

        return controls;
    }

    animate() {
        if (this.render === true) requestAnimationFrame(this.animate.bind(this));

        // this.stats.begin();

        let delta = this.clock.getDelta();

        // update orbitControls if they are added
        this.orbitControls && this.orbitControls.update();

        // play all animations added to the array
        this.animations.map(animation => animation());

        this.renderer.render(this.scene, this.camera);
    }

    loadGltf(path) {
        return new Promise((resolve, reject) => {
            const gltfLoader = new GLTFLoader(this.loadingManager);
            const dracoLoader = new DRACOLoader();

            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
            gltfLoader.setDRACOLoader( dracoLoader );

            if (!path) return;

            gltfLoader.load(path, gltf => {
                resolve(gltf);
            });
        });
    }

    loadTexture(path) {
        return new Promise((resolve, reject) => {
            const textureLoader = new THREE.TextureLoader(this.loadingManager);

            if (!path) return;

            textureLoader.load(path, texture => {
                resolve(texture);
            });
        });
    }
}

export default Scene;